/**
 * The dashboard's `ApiClient` — the one HTTP brain for every feature.
 *
 * Wires region routing + minute-based failover, the `Source-Platform` /
 * `Authorization` headers, the short GET cache and the catalogued error model
 * from `@authe/fe-commons/api`. The bearer token is pulled live from the
 * `SessionManager` (refreshed by the Auth0 SDK as needed), so the client never
 * holds a stale token.
 */
import { ApiClient } from "@authe/fe-commons/api";
import { makePathRewriter, RESOURCE_REWRITES } from "@authe/fe-commons/config";
import { requestKeyHeaders } from "@authe/fe-commons/signing";
import { recordApiResponse } from "@authe/ui-core/observability";
import { appConfig, APPLICATION_SLUG, APPLICATION_AUTH_TYPE } from "@/config";
import { regionRoutingConfig } from "./routing-config";

/**
 * Anti-replay request signer: signs each REST call with the obfuscated
 * REQUEST-KEY plus a single-use REQUEST-NONCE. The private authorizer verifies
 * the key and claims the nonce exactly once, so a replayed request is denied.
 * We sign over the request PATH (not the full URL) so the message matches the
 * backend's `method_path` (API Gateway resource path, stage stripped). The
 * report_only rollout confirms this alignment before signing is enforced.
 */
function signRequest({ url, method }: { url: string; method: string }): Record<string, string> {
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    // `url` is already a bare path — sign it as-is.
  }
  return requestKeyHeaders({ url: path, method });
}

/**
 * Dashboard-local rewrite table: the shared `RESOURCE_REWRITES` doesn't yet
 * carry `subscriptions-mgmt-api`, so we prepend the rule (first-win) and fall
 * back to the shared rules for everything else. subscriptions-mgmt-api mounts on
 * the `.management.` zone at `management/subscriptions-mgmt-api`, so canonical
 * `/subscriptions[/{id}]` maps to `/management/subscriptions-mgmt-api/v0/...`;
 * the region router then injects {VERB}/{CONTINENT} and the `.management.` host.
 * Long-term this rule belongs in `@authe/fe-commons` `resource-paths.ts`.
 */
const rewritePath = makePathRewriter({
  rules: [
    {
      match: "/subscriptions",
      to: "/management/subscriptions-mgmt-api/v0/subscriptions",
      note: "subscriptions-mgmt-api (.management. zone)",
    },
    ...RESOURCE_REWRITES.rules,
  ],
});

export function createApiClient(
  getAccessToken: () => Promise<string | null>,
  /**
   * Recover from a gateway 401/403 token-expiry rejection: refresh the bearer
   * (and update whatever the router reads) → return `true` to retry; or kick
   * off a re-login → return `false`. See {@link ApiClient.onAuthError}.
   */
  onAuthError?: () => Promise<boolean>,
): ApiClient {
  return new ApiClient({
    // Fallback base used only until the session token lands (and in the rare
    // token-without-region_config case). The CloudFront `api.` edge is
    // same-origin in prod; real calls route off it the moment the token loads.
    baseUrl: appConfig.apiUrl,
    backupBaseUrl: appConfig.backupApiUrl,
    region: appConfig.region,
    application: APPLICATION_SLUG,
    authType: APPLICATION_AUTH_TYPE,
    getAccessToken,
    requestSigner: signRequest,
    ...(onAuthError ? { onAuthError } : {}),
    // Map canonical FE paths (`/transactions/blockchain`, …) onto the backend's
    // tier-scoped base paths (`/private/transactions/v0/blockchain`). The region
    // router below then injects the verb + continent and the regional host.
    rewritePath,
    // Regional `.api.` routing (shared brain): resolves every call to
    // `{alias}.{continent}.api.{domain}` from the JWT, injects `{VERB}/{CONTINENT}`
    // into the version segment and the blue/green colour, and does the legacy
    // minute-based failover — instead of the `api.` CloudFront edge (which has no
    // CORS for localhost). e.g. POST /transactions/blockchain →
    // https://pdx.us.api.sandbox.authe.io/private/transactions/blue/ANY/US/v0/transactions/blockchain
    //
    // Shared with the GraphQL client (see `routing-config.ts`) so REST and
    // GraphQL region-switch and fail over together off the same JWT snapshot.
    routing: regionRoutingConfig,
    regionApiRouting: true,
    onResponse: recordApiResponse,
  });
}
